import { useMemo } from 'react';
import { CreateEditFormDrawer } from 'framework/ViewTypes/contentLayout';
import { CREATE_VENDOR_REQUEST_ID } from 'domain/shared/oneStop';
import { FormDraftManager, clearDraft, loadDraft } from 'domain/shared/oneStop/useOneStopFormDraft';
import VendorDocumentContextTemplate from '../../VendorTabs/vendorDocuments/VendorDocumentContextTemplate';
import useVendorService from '../../useVendorService';
import { getCreateVendorRequestSections } from './createVendorRequestSections';

interface CreateVendorRequestDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}

const initialData = { contacts: [] };

const CreateVendorRequestDrawer = ({ isOpen, onClose, onSuccess }: CreateVendorRequestDrawerProps) => {
  const { create, createVendorValidationSchema, searchVendorCostCentersAccounts } = useVendorService();
  const draft = useMemo(() => (isOpen ? loadDraft(CREATE_VENDOR_REQUEST_ID) : null), [isOpen]);

  const getDrawerSections = () => {
    const [vendorInformation, ...otherSections] = getCreateVendorRequestSections(searchVendorCostCentersAccounts);

    return [
      {
        ...vendorInformation,
        content: (
          <>
            {vendorInformation.content}
            <FormDraftManager
              requestId={CREATE_VENDOR_REQUEST_ID}
              baselineInitialData={initialData}
              restoredFromSavedAt={draft?.savedAt ?? null}
            />
          </>
        ),
      },
      ...otherSections,
    ];
  };

  return (
    <VendorDocumentContextTemplate>
      <CreateEditFormDrawer
        key={String(isOpen)}
        isOpen={isOpen}
        onClose={onClose}
        title="Request signing agreement with a new vendor"
        initialData={{ ...initialData, ...draft?.data }}
        submitFormFunc={create}
        getValidationSchemaFunc={createVendorValidationSchema}
        submitButtonTitle="Submit"
        secondaryButtonTitle="Cancel"
        onSecondaryAction={onClose}
        afterSubmit={{
          message: 'Vendor request has been submitted.',
          execute: async () => {
            await onSuccess();
            clearDraft(CREATE_VENDOR_REQUEST_ID);
            onClose();
          },
        }}
        getSections={getDrawerSections}
      />
    </VendorDocumentContextTemplate>
  );
};

export default CreateVendorRequestDrawer;
